import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { LabelSM } from '@/atoms/Text'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { Episode, Project } from '@/services/ProjectsService'
import { useGetRecommendedUpNext } from '@/services/RecommendationService'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { EndOfAssetLayout } from './EndOfAssetLayout'

const PRE_CREDIT_AD_BUFFER = 7

export interface EpisodeOverlayProps {
  canShowEpisodeOverlay: boolean
  isGuildMember?: boolean
  episode?: Episode
  hidePlayerControls: () => void
  showPlayerControls: () => void
  setCanShowEpisodeOverlay: (canShowEpisodeOverlay: boolean) => void
  hasDismissedOverlay: boolean
  setHasDismissedOverlay: (hasDismissedOverlay: boolean) => void
  setShowShareModal: (showShareModal: boolean) => void
  desktopView?: boolean
  project?: Project
  isAdPlaying: boolean
  watchPosition?: number | null
  isGuildShareAvailable?: boolean
}

export const EpisodeOverlay: React.FC<EpisodeOverlayProps> = ({
  canShowEpisodeOverlay,
  isGuildMember,
  episode,
  hidePlayerControls,
  showPlayerControls,
  setCanShowEpisodeOverlay,
  hasDismissedOverlay,
  setHasDismissedOverlay,
  setShowShareModal,
  desktopView = false,
  project,
  isAdPlaying,
  watchPosition,
  isGuildShareAvailable = false,
}) => {
  const { t } = useTranslate('watch')
  const track = useSafeTrack()
  const [overlayEnabled, setOverlayEnabled] = useState(false)

  const projectSlug = episode?.projectSlug ?? ''
  const titleId = project?.title?.id ?? ''
  const projectType = project?.projectType
  const { playingProgress, playing } = useAngelPlayerContext()
  const creditsStartTime = episode?.source?.credits && episode.source.credits > 0 ? episode.source.credits : 0
  const { recommendedUpNext, loading } = useGetRecommendedUpNext(titleId, canShowEpisodeOverlay)

  const backgroundImgUrl =
    project?.title?.landscapeBackdrop?.cloudinaryPath ??
    project?.discoveryPosterLandscapeCloudinaryPath ??
    episode?.posterLandscapeCloudinaryPath ??
    ''
  const logoUrl = project?.title?.logo?.cloudinaryPath ?? project?.logoCloudinaryPath ?? ''
  const projectName = project?.name

  const canShowOverlay = useMemo(() => {
    if (creditsStartTime <= 0) return false

    // Check if current playback position is in the credits
    const isCurrentlyInCredits = playing && playingProgress - PRE_CREDIT_AD_BUFFER > creditsStartTime

    // Check if user has already watched past the credits in a previous session on load before play
    const hasAlreadyWatchedPastCredits =
      !playing &&
      watchPosition !== undefined &&
      watchPosition !== null &&
      watchPosition - PRE_CREDIT_AD_BUFFER > creditsStartTime

    return isCurrentlyInCredits && !hasAlreadyWatchedPastCredits
  }, [creditsStartTime, playingProgress, watchPosition, playing])

  const enableOverlay = useCallback(() => {
    if (!overlayEnabled) {
      if (desktopView) hidePlayerControls()
      setCanShowEpisodeOverlay(true)
      track('End of Asset Overlay Shown', { projectSlug, context: 'Player' })
      setOverlayEnabled(true)
    }
  }, [overlayEnabled, desktopView, hidePlayerControls, setCanShowEpisodeOverlay, track, projectSlug, setOverlayEnabled])

  useEffect(() => {
    const shouldEnableOverlay = () => {
      if (!canShowOverlay || hasDismissedOverlay || isAdPlaying) {
        return false
      } else {
        return true
      }
    }

    if (shouldEnableOverlay()) {
      enableOverlay()
    }
  }, [projectSlug, canShowOverlay, hasDismissedOverlay, isAdPlaying, enableOverlay])

  const handleClose = useCallback(() => {
    track('End of Asset Overlay Closed', { projectSlug, context: 'Player' })
    setHasDismissedOverlay(true)
    setCanShowEpisodeOverlay(false)
    showPlayerControls()
  }, [showPlayerControls, setCanShowEpisodeOverlay, setHasDismissedOverlay, track, projectSlug])

  const handleShareButtonClick = useCallback(() => {
    track('Share With Friend End of Asset Button Clicked', { projectSlug, context: 'Player' })
    setShowShareModal(true)
  }, [track, projectSlug, setShowShareModal])

  const handleJoinGuildClick = useCallback(() => {
    track('Join The Guild End of Asset Button Clicked', { projectSlug, context: 'Player' })
  }, [track, projectSlug])

  const handleRecommendationClick = useCallback(
    (slug?: string | null) => {
      track('End of Asset Recommendation Clicked', { projectSlug: slug ?? '', context: 'Player' })
    },
    [track],
  )

  const handleDiscoverMoreClick = useCallback(() => {
    track('End of Asset Discover More Clicked', { projectSlug, context: 'Player' })
  }, [track, projectSlug])

  return (
    <>
      <When condition={desktopView}>
        {/* Desktop Version */}
        <div
          className={classNames('hidden', {
            'lg:absolute w-full h-full lg:flex opacity-100 transition-all ease-in-out': canShowEpisodeOverlay,
            'opacity-0 transition-all ease-in-out relative w-full lg:w-0 h-0': !canShowEpisodeOverlay,
          })}
          style={{
            transitionDuration: '750ms',
          }}
        >
          <div
            className="absolute right-12 top-2 z-40 flex cursor-pointer flex-row items-center gap-2 text-white"
            onClick={handleClose}
          >
            <CloseIcon size={20} color={'white'} />
            <LabelSM>{t('close', 'Close')}</LabelSM>
          </div>
          <ResponsiveBackgroundImage src={backgroundImgUrl} />
          <div className="absolute inset-0 bg-gradient-to-t from-core-gray-950" />
          <div className="absolute inset-0 bg-gradient-to-b from-core-gray-950" />
          <div className="lg:relative lg:ml-14 lg:mt-14 lg:w-full">
            <EndOfAssetLayout
              logoUrl={logoUrl}
              isGuildMember={isGuildMember}
              t={t}
              projectName={projectName}
              handleShareButtonClick={handleShareButtonClick}
              recommendedUpNext={recommendedUpNext}
              loading={loading}
              episode={episode}
              handleJoinGuildClick={handleJoinGuildClick}
              handleRecommendationClick={handleRecommendationClick}
              handleDiscoverMoreClick={handleDiscoverMoreClick}
              projectType={projectType}
              isGuildShareAvailable={isGuildShareAvailable}
            />
          </div>
        </div>
      </When>
      <When condition={!desktopView}>
        {/* Mobile/Tablet Version */}
        <div
          className={classNames('lg:hidden', {
            'relative mt-4 h-full w-full': canShowEpisodeOverlay,
            'opacity-0 transition-all ease-in-out relative w-full h-0 max-h-0 overflow-hidden': !canShowEpisodeOverlay,
          })}
          style={{
            transitionDuration: '750ms',
          }}
        >
          <EndOfAssetLayout
            logoUrl={logoUrl}
            isGuildMember={isGuildMember}
            t={t}
            projectName={projectName}
            handleShareButtonClick={handleShareButtonClick}
            recommendedUpNext={recommendedUpNext}
            handleClose={handleClose}
            loading={loading}
            episode={episode}
            handleJoinGuildClick={handleJoinGuildClick}
            handleRecommendationClick={handleRecommendationClick}
            handleDiscoverMoreClick={handleDiscoverMoreClick}
            projectType={projectType}
            isGuildShareAvailable={isGuildShareAvailable}
          />
        </div>
      </When>
    </>
  )
}
